import {useUxData} from "@/stores/ux-data";
import { rulesFor } from "@/appdata/item-type-rules";
import { containerDisplayName } from '@/appdata/container-fields';

// Widened to `unknown` so every container shape (item, skill rank, edge, hindrance) can share one
// comparator — containerDisplayName already accepts `unknown` and composes the label per-shape.
//
// A factory over the name function, because a list must sort by exactly the string it renders: the
// sheet's edge, hindrance and skill lists render their chosen labels as a trapping fallback
// (chosen-labels.ts), which needs the character, so those sorts pass a bound name function here.
export const compareContainersBy = (nameOf: (container: unknown) => string) => (a: unknown, b: unknown): number => {
    const name1 = nameOf(a).toLowerCase();
    const name2 = nameOf(b).toLowerCase();

    if (name1 > name2) return 1;
    if (name1 < name2) return -1;
    return 0;
};
export const compareContainersByName = compareContainersBy(containerDisplayName);

const GLOBAL_STATUS_ICONS: Record<string, string> = {
    'primary': 'fa-hand',
    'secondary': 'fa-hand fa-flip-horizontal',
    'two-handed': 'fa-hands',
    'carried': 'fa-backpack',
    'stored': 'fa-box-archive',
};

export function useEquipmentStatus() {
    const ux = useUxData();

    // type is null (not just absent) at the two call sites that read it off
    // SwadeItemInterface.type, which is `string | null` — rulesFor already treats null and
    // undefined identically, so this only widens what the parameter accepts.
    const statusIcon = (status: string, type?: string | null): string => {
        const typeIcon = rulesFor(type).statusIcons?.[status];
        if (typeIcon) return typeIcon;
        return GLOBAL_STATUS_ICONS[status] ?? 'fa-box-archive';
    };

    const statusesForType = (type: string | null): Record<string, string> => {
        const fromLabels = ux.labels?.swade?.data?.equipment?.statuses?.[type ?? ''];
        if (fromLabels && Object.keys(fromLabels).length > 0) return fromLabels;
        return rulesFor(type).statusLabelFallback ?? {};
    };

    return { statusIcon, statusesForType };
}
